// dependencies
import { cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../../lib/utils";

import { PopIn } from "../../organisms/AnimatePresence";
// components
import { Tooltip } from "../Tooltip";

export interface BadgeProps {
	/**
	 * If true, the badge will have a border
	 */
	border?: boolean;
	/**
	 * The tooltip to display when hovering over the badge
	 */
	tooltip?: string | React.ReactNode;
	/**
	 * A class name to apply to the badge
	 */
	dangerouslySetClassName?: string;
	/**
	 * The content to display in the badge
	 */
	children: React.ReactNode;
	/**
	 * If true, the badge will animate in/out with a scale/pop effect when added/removed from the DOM.
	 * Use with AnimatePresence for exit animations.
	 * @default false
	 */
	animateEnter?: boolean;
}

const badgeVariants = cva("text- flex size-6 items-center justify-center rounded-full bg-neutral-2 text-neutral-9", {
	variants: {
		border: {
			true: "border border-solid border-neutral-0",
		},
	},
	defaultVariants: {
		border: false,
	},
});

/**
 * The badge component is used to display the count of items. To use it, include it as a child of the component you want to attach it to.
 */
const Badge = (props: BadgeProps) => {
	const { children, border, dangerouslySetClassName, tooltip, animateEnter = false } = props;

	const badgeClasses = cn(badgeVariants({ border }), `${dangerouslySetClassName}`);

	const badgeContent = animateEnter ? (
		<PopIn dangerouslySetClassName={badgeClasses}>{children}</PopIn>
	) : (
		<div className={badgeClasses}>{children}</div>
	);

	return (
		<div className="absolute -right-4 -top-3">
			{tooltip ? (
				<Tooltip content={tooltip} asChild>
					{badgeContent}
				</Tooltip>
			) : (
				badgeContent
			)}
		</div>
	);
};

export { Badge };
